import {
  type IAIReceptionistSectionQaPairs,
  type IImportProposal,
  type IImportProposalFields,
  type IImportProposedCustomSection,
  type IImportProposedQaPair,
  type IImportProposedValue,
  IMPORT_PROPOSAL_BOOLEAN_FIELDS,
  IMPORT_PROPOSAL_PROSE_FIELDS,
  IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS,
  IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS,
  type SectionQaPairLists,
} from '@principle-theorem/principle-core/interfaces';
import { compact, omit } from 'lodash';

/** A proposed value, Q&A pair or custom section: anything that cites a page. */
type CitedItem = Pick<IImportProposedValue<unknown>, 'provenance'>;

export const IMPORT_PROPOSAL_FIELDS = [
  ...IMPORT_PROPOSAL_PROSE_FIELDS,
  ...IMPORT_PROPOSAL_BOOLEAN_FIELDS,
  ...IMPORT_PROPOSAL_WINDOW_HOURS_FIELDS,
];
// What separates the addresses of a Provenance that lists several pages:
// line breaks, spaces or commas.
const provenanceListSeparatorPattern = /[\s,]+/;
/**
 * Text naming the website or a page, denying it, then naming what is
 * missing, such as "The website does not specify how public holidays affect
 * the practice." Each apostrophe may be plain or typographic. The source
 * word (website/site/page/pages) must be a standalone word, not joined by
 * hyphens (e.g., "on-site" is not matched), and must be the subject that
 * lacks info.
 */
const websiteLacksInformationPattern =
  /(?<![-\w])(website|site|page|pages)(?![-\w])[^.]{0,60}\b(does not|doesn['\u2019]t|do not|don['\u2019]t|did not|didn['\u2019]t|is not|isn['\u2019]t|has no|have no)\b[^.]{0,60}\b(specify|specified|provide|provided|mention|mentioned|state|stated|include|included|detail|details|information|say)\b/i;
/** Text opening by saying there is nothing to tell, such as "Not specified." */
const noInformationPattern =
  /^\s*(no (specific |further )?(information|details)|not (specified|mentioned|provided|stated))\b/i;
/**
 * Text saying there is no information or details on the website about
 * something, such as "There is no information on the website about public
 * holidays." The source word must be a standalone word, not joined by
 * hyphens.
 */
const noInformationOnWebsitePattern =
  /\bno (specific |further )?(information|details?)\b[^.]{0,40}\b(on|in|from) the (?<![-\w])(website|site|page|pages)(?![-\w])\b/i;

/**
 * Owns the shape of an Import Proposal, which the other Website Import rules
 * build on, and keeps only the values the extractor read from the website.
 */
export class ImportProposal {
  /**
   * Keeps only values whose Provenance names the snapshot's page URLs and
   * nothing else, and leaves each citing exactly one page. Page text is
   * untrusted input to the extractor, so a value citing any other address is
   * treated as invented rather than read from the website. The extractor is
   * asked for one page, but a value it drew from several, such as an Our
   * Team value written from a team page and a clinician's own page,
   * sometimes lists them all, one per line. Such a value was still read from
   * the website, so when every address listed is a page URL it is kept and
   * cites the first one listed.
   */
  static filterByProvenance(
    proposal: IImportProposal,
    pageUrls: readonly string[]
  ): IImportProposal {
    const pageUrlSet = new Set(pageUrls);
    return {
      ...proposal,
      fields: ImportProposal._citeFields(proposal.fields, pageUrlSet),
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        proposal.sectionQaPairs,
        (pairs) => ImportProposal._citeEach(pairs, pageUrlSet)
      ),
      customSections: ImportProposal._citeEach(
        proposal.customSections,
        pageUrlSet
      ).map((section) =>
        ImportProposal._filterCustomSectionQaPairs(section, pageUrlSet)
      ),
    };
  }

  /**
   * Leaves out prose values, Q&A pairs and custom sections that only say the
   * website does not give the information, such as "The website does not
   * provide specific details about the cancellation policy." The extractor is
   * told never to write one, but its output is untrusted, and a non-answer the
   * practice accepted would be read to callers as the practice's own answer.
   * A Q&A pair is judged by its answer and a custom section by its content.
   */
  static dropNonAnswers(proposal: IImportProposal): IImportProposal {
    return {
      ...proposal,
      fields: omit(
        proposal.fields,
        IMPORT_PROPOSAL_PROSE_FIELDS.filter((field) =>
          ImportProposal._isNonAnswer(proposal.fields[field]?.value)
        )
      ),
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        proposal.sectionQaPairs,
        (pairs) => ImportProposal._dropNonAnswerQaPairs(pairs)
      ),
      customSections: proposal.customSections
        .filter((section) => !ImportProposal._isNonAnswer(section.content))
        .map((section) =>
          section.qa
            ? {
                ...section,
                qa: ImportProposal._dropNonAnswerQaPairs(section.qa),
              }
            : section
        ),
    };
  }

  /**
   * Applies `mapPairs` to every Q&A section and leaves out sections left with
   * no pairs, so neither a proposal nor a request lists an empty section.
   */
  static mapSectionQaPairs<Pair, MappedPair>(
    sectionQaPairs: SectionQaPairLists<Pair> | undefined,
    mapPairs: (
      pairs: Pair[],
      key: keyof IAIReceptionistSectionQaPairs
    ) => MappedPair[]
  ): SectionQaPairLists<MappedPair> {
    const mapped: SectionQaPairLists<MappedPair> = {};
    IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.forEach((key) => {
      const pairs = mapPairs(sectionQaPairs?.[key] ?? [], key);
      if (pairs.length > 0) {
        mapped[key] = pairs;
      }
    });
    return mapped;
  }

  /**
   * Sets `key` only when there is a value, so results never carry explicit
   * `undefined` keys (a callable response would turn them into `null`).
   */
  static assignIfDefined<Target, Key extends keyof Target>(
    target: Target,
    key: Key,
    value: Target[Key] | undefined
  ): void {
    if (value !== undefined) {
      target[key] = value;
    }
  }

  private static _citeFields(
    fields: IImportProposalFields,
    pageUrlSet: ReadonlySet<string>
  ): IImportProposalFields {
    const cited: IImportProposalFields = {};
    IMPORT_PROPOSAL_FIELDS.forEach((field) =>
      ImportProposal.assignIfDefined(
        cited,
        field,
        ImportProposal._citedFrom(fields[field], pageUrlSet)
      )
    );
    return cited;
  }

  private static _citeEach<Item extends CitedItem>(
    items: Item[],
    pageUrlSet: ReadonlySet<string>
  ): Item[] {
    return compact(
      items.map((item) => ImportProposal._citedFrom(item, pageUrlSet))
    );
  }

  private static _citedFrom<Item extends CitedItem>(
    item: Item | undefined,
    pageUrlSet: ReadonlySet<string>
  ): Item | undefined {
    if (!item) {
      return undefined;
    }
    if (pageUrlSet.has(item.provenance)) {
      return item;
    }
    const listedAddresses = compact(
      item.provenance.split(provenanceListSeparatorPattern)
    );
    const citesOnlyPages =
      listedAddresses.length > 0 &&
      listedAddresses.every((address) => pageUrlSet.has(address));
    return citesOnlyPages
      ? { ...item, provenance: listedAddresses[0] }
      : undefined;
  }

  private static _filterCustomSectionQaPairs(
    section: IImportProposedCustomSection,
    pageUrlSet: ReadonlySet<string>
  ): IImportProposedCustomSection {
    if (!section.qa) {
      return section;
    }
    return {
      ...section,
      qa: ImportProposal._citeEach(section.qa, pageUrlSet),
    };
  }

  /**
   * Text must name the website or a page, deny it and name what is missing,
   * or open by saying there is nothing to tell, so an answer that says "not"
   * about something else, such as "We do not charge for parking.", is kept.
   */
  private static _isNonAnswer(text: string | undefined): boolean {
    return (
      text !== undefined &&
      (websiteLacksInformationPattern.test(text) ||
        noInformationPattern.test(text) ||
        noInformationOnWebsitePattern.test(text))
    );
  }

  private static _dropNonAnswerQaPairs(
    pairs: IImportProposedQaPair[]
  ): IImportProposedQaPair[] {
    return pairs.filter((pair) => !ImportProposal._isNonAnswer(pair.answer));
  }
}
